import { useEffect, useRef, useState } from "react";
import useAdminDataStore, { selectCurrentGroupData } from "../../../../store/adminData/adminData";
import {
  fbSaveLocationDetails,
  fbSaveParentLocationDetails,
  getLocationDetails,
  getParentLocationDetails,
} from "../../../../services/firebaseService/endPoints/admin/content";

import { useNavigate, useParams } from "react-router-dom";
import { GOOGLE_MAPS_URL } from "../../../../config/maps";
import { toast } from "react-toastify";
import logger from "../../../../utils/logger";
import useDashboardInfo from "../../../../hooks/useDashboardInfo";
import useChildrenGroups from "../../../clubAdmin/hooks/useChildrenGroups";

const useCreateLocation = (onCloseModal) => {
  const childRef = useRef(null);
  const { id: locationId } = useParams();
  const { dashboardType } = useDashboardInfo();
  const navigate = useNavigate();

  const adminData = useAdminDataStore(selectCurrentGroupData);

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [scriptLoaded, setScriptLoaded] = useState(false);

  const [formData, setFormData] = useState({});
  const [errorInForm, setErrorInForm] = useState({});
  const [locationData, setLocationData] = useState({});
  const [locationImage, setLocationImage] = useState("");

  // Group selector for clubadmin
  const { childGroupDetails } = useChildrenGroups();
  const [selectedGroups, setSelectedGroups] = useState({
    selectionType: "all",
    selectedValues: null,
    groupIds: null,
  });

  const handleGroupSelectionChange = (groupSelection) => {
    setSelectedGroups({
      selectionType: groupSelection.selectionType || "all",
      selectedValues: groupSelection.selectedValues || null,
      groupIds: groupSelection.groupIds || null,
    });
  };

  useEffect(() => {
    if (locationId) {
      handleEditMode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId, adminData.id]);

  const handleEditMode = async () => {
    let data = {};

    try {
      setLoading(true);

      if (dashboardType === "clubadmin") {
        data = await getParentLocationDetails(adminData?.id, locationId);
      } else {
        data = await getLocationDetails(adminData?.id, locationId);
      }

      const { locationImage, locationData, ...formData } = data;

      setLocationData(locationData);
      setLocationImage(locationImage);
      setFormData({ ...formData });

      if (dashboardType === "clubadmin" && data.audience) {
        setSelectedGroups(data.audience);
      }
      setLoading(false);
    } catch {
      setLoading(false);
    }
  };

  const mapUpdate = (e) => {
    setLocationData((prevState) => {
      const locationData = {
        ...prevState.locationData,
      };
      // placeId: e.placeId,
      locationData.address = e.address;
      locationData.placeId = e.placeId;
      locationData.longitude = e.long;
      locationData.latitude = e.lat;
      return { ...locationData };
    });
  };

  const handleMapOK = () => {
    if (childRef.current) {
      childRef.current.updateFormMapData();
    }
    setShowModal(false);
  };

  const toggleModal = () => {
    setShowModal((prev) => !prev);
  };

  const handleModalChange = () => {
    if (!scriptLoaded) {
      const script = document.createElement("script");
      script.src = GOOGLE_MAPS_URL;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        logger.log("Google Maps script loaded successfully");
        setScriptLoaded(true);
        toggleModal();
      };
      script.onerror = (error) => {
        logger.error("Error loading Google Maps:", error);
        toast.error("Failed to load Google Maps. Please try again.");
      };
      document.head.appendChild(script);
    } else {
      toggleModal();
    }
  };

  const handleInputChange = (e, customName, customValue) => {
    const name = e?.target?.name || customName;
    const value = e?.target?.value || customValue;

    if (name) {
      setFormData((prev) => ({
        ...prev,
        [name]: value || "",
      }));
    }
  };

  const handleSubmit = () => {
    setSaving(true);

    if (!formData?.type) {
      setErrorInForm({
        isError: true,
        visible: true,
        message: "Please select a type before proceeding.",
      });

      setSaving(false);
      window.scrollTo(0, 0);
      return;
    }

    if (!formData?.name) {
      setErrorInForm({
        isError: true,
        visible: true,
        message: "Please fill in the name before proceeding.",
      });

      setSaving(false);
      window.scrollTo(0, 0);
      return;
    }

    if (!locationImage) {
      setErrorInForm({
        isError: true,
        visible: true,
        message: "Please upload a location image before proceeding.",
      });

      setSaving(false);
      window.scrollTo(0, 0);
      return;
    }

    const isValidCoordinate = (val) => typeof val === "number" && !isNaN(val);

    if (
      !isValidCoordinate(locationData?.latitude) ||
      !isValidCoordinate(locationData?.longitude) ||
      !locationData?.placeId
    ) {
      setErrorInForm({
        isError: true,
        visible: true,
        message: "Please select a location on the map to set the coordinates.",
      });

      setSaving(false);
      window.scrollTo(0, 0);
      return;
    }

    const data = {
      ...formData,
      locationData,
      locationImage,
    };

    let promise = fbSaveLocationDetails(
      data,
      adminData.id,
      locationId,
      adminData.isParent
    );

    // Add groups field if clubadmin
    if (dashboardType === "clubadmin" && selectedGroups?.selectionType) {
      data.audience = {
        selectionType: selectedGroups.selectionType,
        selectedValues: selectedGroups.selectedValues,
        groupIds: selectedGroups.groupIds,
      };

      // Add groups array based on selection type
      if (selectedGroups.selectionType === "all") {
        data.groups = ["global"];
      } else if (selectedGroups.selectionType === "official") {
        data.groups = ["Official Groups"];
      } else {
        // For region or specific groups, use the groupIds
        data.groups = selectedGroups.groupIds || [];
      }

      promise = fbSaveParentLocationDetails(data, adminData.id, locationId);
    }

    promise.then(() => {
      setSaving(false);

      const navigatePath = `/${dashboardType}/content/locations`;

      if (locationId) {
        let result = window.confirm(
          "Please update related future events to reflect location changes. The Directory and Group Website are updated automatically."
        );
        if (result) {
          navigate(navigatePath);
        }
      } else {
        navigate(navigatePath);
      }

      onCloseModal && onCloseModal();
    });
  };

  const locationClear = () => {
    setLocationData({ address: "" });
  };

  const updateAddress = (address) => {
    setLocationData({ address: address });
  };
  const handleErrorMessageClose = () => {
    setErrorInForm((prev) => ({ ...prev, visible: false }));
  };

  return {
    saving,
    loading,
    childRef,
    formData,
    adminData,
    showModal,
    locationId,
    errorInForm,
    locationData,
    locationImage,
    mapUpdate,
    handleMapOK,
    toggleModal,
    handleSubmit,
    updateAddress,
    locationClear,
    setLocationImage,
    handleInputChange,
    handleModalChange,
    handleErrorMessageClose,
    dashboardType,
    selectedGroups,
    childGroupDetails,
    handleGroupSelectionChange,
  };
};

export default useCreateLocation;
